import React, { Suspense, lazy, useState } from "react";
import { Check } from "lucide-react";
import { BusinessConfigData, BusinessConfigField } from "../../../../../../../interfaces/BusinessObject";
import { BRAND_COLOR_SWATCHES, BRAND_FONT_OPTIONS } from "../../../../../../../constants/BusinessBrandingDefs";
import { describeTemplate, readSlotCalendarTemplate } from "../../../../../../../services/helpers/SlotCalendarTemplateHelper";
import { SlotCalendarTemplate } from "../../../../../../../interfaces/SlotCalendarObject";
import "./styles/BusinessConfigSection.css";

// Lazy: the schedule wizard only loads when the owner opens it.
const SlotCalendarTemplateWizard = lazy(
  () => import("../../../../../Product/ProductDetailsV2/childComponents/SlotAvailabilitySection/template/SlotCalendarTemplateWizard")
);

interface BusinessConfigSectionEditorProps {
  value: unknown;
  onChange: (next: unknown) => void;
}

/** Plain boolean toggles (showcase/collection/gallery/.../orders) carry no
 *  `value` — `isEnabled` alone IS the setting. Only brandColor and
 *  slotCalendar have something stored in `value` (see BusinessConfigData's
 *  doc comment). */
function toggleField(isEnabled: boolean): BusinessConfigField<never> {
  return { isEnabled };
}

const BusinessConfigSectionEditor: React.FC<BusinessConfigSectionEditorProps> = ({
  value,
  onChange,
}) => {
  const safe: BusinessConfigData =
    value && typeof value === "object" ? (value as BusinessConfigData) : {};

  const selectedColorHex = safe.brandColor?.value ?? null;
  const selectedFont = safe.displayFont?.value ?? "default";

  const showcase = safe.showcaseProductImages?.isEnabled !== false;
  const collection = safe.enableCollection?.isEnabled !== false;
  // Sections are opt-in — default off.
  const gallerySections = safe.enableGallerySections?.isEnabled === true;
  // Default off — first product section stays expanded unless this is turned on.
  const collapseAllProductSections = safe.collapseAllProductSections?.isEnabled === true;
  // NineFinds ordering — opt-in, default off.
  const onlineOrdering = safe.enableOnlineOrdering?.isEnabled === true;
  const offlineOrdering = safe.enableOfflineOrdering?.isEnabled === true;
  // The schedule is mandatory: the calendar only counts as on once the
  // business has one. Turning the switch on without it opens the wizard;
  // cancelling leaves it off. Turning it off keeps the (locked) schedule —
  // a PUT fully replaces the slotCalendar column, so `value` must be resent
  // alongside isEnabled: false or the stored template would be lost.
  const slotTemplate = readSlotCalendarTemplate(safe.slotCalendar?.value);
  const slotCalendar = safe.slotCalendar?.isEnabled === true && !!slotTemplate;
  const [templateWizardOpen, setTemplateWizardOpen] = useState(false);

  const setSlotCalendar = (isEnabled: boolean, template: SlotCalendarTemplate | null) =>
    onChange({
      ...safe,
      slotCalendar: template ? { isEnabled, value: template } : { isEnabled },
    });

  const toggleSlotCalendar = () => {
    if (slotCalendar) setSlotCalendar(false, slotTemplate);
    else if (slotTemplate) setSlotCalendar(true, slotTemplate);
    else setTemplateWizardOpen(true);
  };

  return (
    <div className="business-config-editor">
      {/* ── Appearance ── */}
      <div className="business-config-editor__appearance">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Brand color</span>
          <span className="business-config-editor__hint">
            Colors your page's badges, buttons, and hero banner. Auto picks it from your logo.
          </span>
        </div>
        <div className="business-config-editor__swatches">
          {BRAND_COLOR_SWATCHES.map((swatch) => {
            const isSelected = selectedColorHex === swatch.hex;
            return (
              <button
                key={swatch.label}
                type="button"
                className={`business-config-editor__swatch ${isSelected ? "is-selected" : ""} ${
                  swatch.hex === null ? "business-config-editor__swatch--auto" : ""
                }`}
                style={swatch.hex ? { background: swatch.hex } : undefined}
                onClick={() =>
                  onChange({ ...safe, brandColor: swatch.hex ? { value: swatch.hex } : {} })
                }
                aria-label={swatch.label}
                aria-pressed={isSelected}
                title={swatch.label}
              >
                {isSelected && <Check size={14} color={swatch.hex ? "#fff" : "#6a11cb"} />}
              </button>
            );
          })}
        </div>

        <div className="business-config-editor__info" style={{ marginTop: "0.85rem" }}>
          <span className="business-config-editor__label">Display font</span>
          <span className="business-config-editor__hint">
            Applies to your business name on your page header.
          </span>
        </div>
        <select
          className="business-config-editor__font-select"
          value={selectedFont}
          onChange={(e) =>
            onChange({
              ...safe,
              // "default" stored as {} (absent value), same convention as
              // brandColor's Auto swatch — nothing to persist for the default.
              displayFont: e.target.value === "default" ? {} : { value: e.target.value },
            })
          }
        >
          {BRAND_FONT_OPTIONS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div className="business-config-editor__row">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Showcase product images</span>
          <span className="business-config-editor__hint">
            Show product images on your business page
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={showcase}
          className={`business-config-editor__toggle ${showcase ? "is-on" : ""}`}
          onClick={() => onChange({ ...safe, showcaseProductImages: toggleField(!showcase) })}
        >
          <span className="business-config-editor__toggle-thumb" />
        </button>
      </div>
      <div className="business-config-editor__row">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Enable Collection</span>
          <span className="business-config-editor__hint">
            Let customers build a collection from your products
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={collection}
          className={`business-config-editor__toggle ${collection ? "is-on" : ""}`}
          onClick={() => onChange({ ...safe, enableCollection: toggleField(!collection) })}
        >
          <span className="business-config-editor__toggle-thumb" />
        </button>
      </div>
      <div className="business-config-editor__row">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Gallery sections</span>
          <span className="business-config-editor__hint">
            Group gallery media into collapsible sections
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={gallerySections}
          className={`business-config-editor__toggle ${gallerySections ? "is-on" : ""}`}
          onClick={() =>
            onChange({ ...safe, enableGallerySections: toggleField(!gallerySections) })
          }
        >
          <span className="business-config-editor__toggle-thumb" />
        </button>
      </div>
      <div className="business-config-editor__row">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Collapse product sections</span>
          <span className="business-config-editor__hint">
            Start every product section collapsed (off: the first section stays open)
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={collapseAllProductSections}
          className={`business-config-editor__toggle ${collapseAllProductSections ? "is-on" : ""}`}
          onClick={() =>
            onChange({
              ...safe,
              collapseAllProductSections: toggleField(!collapseAllProductSections),
            })
          }
        >
          <span className="business-config-editor__toggle-thumb" />
        </button>
      </div>
      <div className="business-config-editor__row">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Accept delivery orders</span>
          <span className="business-config-editor__hint">
            Let customers order for delivery via NineFinds
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={onlineOrdering}
          className={`business-config-editor__toggle ${onlineOrdering ? "is-on" : ""}`}
          onClick={() =>
            onChange({ ...safe, enableOnlineOrdering: toggleField(!onlineOrdering) })
          }
        >
          <span className="business-config-editor__toggle-thumb" />
        </button>
      </div>
      <div className="business-config-editor__row">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Accept dine-in orders</span>
          <span className="business-config-editor__hint">
            Let customers order to collect or use at your place via NineFinds
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={offlineOrdering}
          className={`business-config-editor__toggle ${offlineOrdering ? "is-on" : ""}`}
          onClick={() =>
            onChange({ ...safe, enableOfflineOrdering: toggleField(!offlineOrdering) })
          }
        >
          <span className="business-config-editor__toggle-thumb" />
        </button>
      </div>
      <div className="business-config-editor__row">
        <div className="business-config-editor__info">
          <span className="business-config-editor__label">Availability calendar</span>
          <span className="business-config-editor__hint">
            {slotTemplate
              ? "Show bookable dates and time slots on your listings and manage bookings"
              : "Turning this on first asks for your business's availability schedule"}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={slotCalendar}
          className={`business-config-editor__toggle ${slotCalendar ? "is-on" : ""}`}
          onClick={toggleSlotCalendar}
        >
          <span className="business-config-editor__toggle-thumb" />
        </button>
      </div>
      {slotTemplate && (
        <div className="business-config-editor__row">
          <div className="business-config-editor__info">
            <span className="business-config-editor__label">Availability schedule</span>
            <span className="business-config-editor__hint">{`${describeTemplate(slotTemplate)} - fixed for all listings`}</span>
          </div>
        </div>
      )}
      {templateWizardOpen && (
        <Suspense fallback={null}>
          <SlotCalendarTemplateWizard
            onCancel={() => setTemplateWizardOpen(false)}
            onSave={(template) => {
              setSlotCalendar(true, template);
              setTemplateWizardOpen(false);
            }}
          />
        </Suspense>
      )}
    </div>
  );
};

export default BusinessConfigSectionEditor;
